/**
 * 1.4.2: 企业信息 Tab
 * 用于显示成员用户所属企业的信息
 * 1.4.2: 添加订阅状态、实时统计功能
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Users, 
  Crown, 
  Calendar,
  Shield,
  HardDrive,
  Link2,
  Loader2,
  User,
  Mail,
  Clock,
  CheckCircle
} from 'lucide-react';
import { getCurrentOrganization, getOrganizationDetails } from '../../services/organizationService';
import { getOrganizationSubscription } from '../../services/billingService'; // 1.4.5: 使用组织ID获取订阅
import { isSupabaseAvailable, supabase } from '../../lib/supabase';
import { formatOrganizationName } from '../../utils/organizationUtils'; // 1.5.1
import { PLAN_CONFIGS } from '../../types/organization';
import type { UserOrganization, OrganizationMember } from '../../types/organization';
import type { SubscriptionInfo } from '../../types/billing';

export function OrganizationInfoTab() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<UserOrganization | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [memberInfo, setMemberInfo] = useState<{
    joined_at?: string;
    invited_by_email?: string;
  } | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<OrganizationMember | null>(null);
  const [memberCount, setMemberCount] = useState<number>(0);

  useEffect(() => {
    loadOrganizationInfo();
  }, []);

  const loadOrganizationInfo = async () => {
    if (!isSupabaseAvailable) {
      setIsLoading(false);
      return;
    }

    try {
      // 1.4.5: 先获取当前组织，再用组织ID获取订阅信息
      const orgResult = await getCurrentOrganization();

      const org = orgResult.organization;
      if (!org) {
        setIsLoading(false);
        return;
      }
      setOrganization(org);
      
      // 1.4.5: 使用当前组织ID获取订阅信息，确保member看到正确的组织订阅状态
      const subResult = await getOrganizationSubscription(org.id);
      if (subResult.subscription) {
        setSubscription(subResult.subscription);
      }

      // 获取组织详情（包含成员列表）
      const { details } = await getOrganizationDetails(org.id);
      if (details) {
        // 动态获取成员人数
        setMemberCount(details.members.length);
        
        // 获取当前用户 ID
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // 找到当前用户的成员信息
          const currentMember = details.members.find(m => m.user_id === user.id);
          if (currentMember) {
            setMemberInfo({
              joined_at: currentMember.joined_at,
            });
            
            // 如果有邀请人，获取邀请人信息
            if (currentMember.invited_by) {
              const inviter = details.members.find(m => m.user_id === currentMember.invited_by);
              if (inviter) {
                setMemberInfo(prev => ({
                  ...prev,
                  invited_by_email: inviter.email,
                }));
              }
            }
          }

          // 获取组织所有者信息
          const owner = details.members.find(m => m.role === 'owner');
          if (owner) {
            setOwnerInfo(owner);
          }
        }
      }
    } catch (error) {
      console.error('Error loading organization info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 格式化日期
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 获取套餐配置
  const getPlanConfig = () => {
    if (!organization) return null;
    return PLAN_CONFIGS[organization.plan_type];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">{t('orgInfo.noOrganization', '未找到组织信息')}</p>
      </div>
    );
  }

  const planConfig = getPlanConfig();

  return (
    <div className="space-y-6">
      {/* 企业信息卡片 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* 头部 - 企业名称和角色 */}
        <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{formatOrganizationName(organization.name, t)}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm">
                  {organization.role === 'owner' ? t('orgInfo.roleOwner', '所有者') : t('orgInfo.roleMember', '成员')}
                </span>
                <span className="text-purple-100 text-sm">@{organization.slug}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 信息详情 */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 左侧 - 基本信息 */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                {t('orgInfo.basicInfo', '基本信息')}
              </h3>
              
              {/* 企业类型 */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('orgInfo.orgType', '组织类型')}</p>
                  <p className="font-medium text-gray-900">
                    {organization.org_type === 'business' 
                      ? t('orgInfo.businessOrg', '企业组织') 
                      : t('orgInfo.personalOrg', '个人组织')}
                  </p>
                </div>
              </div>

              {/* 当前套餐 - 显示订阅状态 */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Crown className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">{t('orgInfo.currentPlan', '当前套餐')}</p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{planConfig?.name || organization.plan_type}</p>
                    {/* 订阅状态标签 */}
                    {subscription?.subscription_status === 'trialing' && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t('orgInfo.trialing', '试用中')}
                      </span>
                    )}
                    {subscription?.subscription_status === 'active' && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {t('orgInfo.subscribed', '已付费')}
                      </span>
                    )}
                    {subscription?.subscription_status === 'past_due' && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                        {t('orgInfo.pastDue', '待支付')}
                      </span>
                    )}
                    {subscription?.subscription_status === 'canceled' && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {t('orgInfo.canceled', '已取消')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 成员人数 - 动态显示 */}
              {/* 1.5.1: 修复分母显示，Standard 套餐使用 purchased_seats */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('orgInfo.memberCount', '成员人数')}</p>
                  <p className="font-medium text-gray-900">
                    {memberCount || organization.current_members} / {
                      // 1.5.1: Standard 套餐使用 purchased_seats，其他使用 max_members
                      subscription?.plan_type === 'standard' 
                        ? (subscription?.purchased_seats || organization.max_members)
                        : (subscription?.max_members || organization.max_members)
                    } {t('people', '人')}
                  </p>
                </div>
              </div>
            </div>

            {/* 右侧 - 资源与我的信息 */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                {t('orgInfo.resourcesAndMembership', '资源与成员资格')}
              </h3>

              {/* 存储空间 - 实时统计 */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <HardDrive className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">{t('orgInfo.storageUsage', '存储用量')}</p>
                  <p className="font-medium text-gray-900">
                    {subscription?.storage_used_gb?.toFixed(1) || '0.0'} / {subscription?.max_storage_gb || planConfig?.storage_gb || 0} GB
                  </p>
                  {/* 进度条 */}
                  <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((subscription?.storage_used_gb || 0) / (subscription?.max_storage_gb || 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* 公开链接配额 - 实时统计 */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">{t('orgInfo.publicLinkUsage', '访问次数统计')}</p>
                  <p className="font-medium text-gray-900">
                    {(subscription?.public_link_used || 0).toLocaleString()} / {(subscription?.public_link_quota || planConfig?.public_link_quota || 0).toLocaleString()} {t('perMonth', '/月')}
                  </p>
                  {/* 进度条 */}
                  <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-orange-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((subscription?.public_link_used || 0) / (subscription?.public_link_quota || 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* 加入时间 */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('orgInfo.joinedAt', '加入时间')}</p>
                  <p className="font-medium text-gray-900">{formatDate(memberInfo?.joined_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 组织管理员信息 */}
      {ownerInfo && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            {t('orgInfo.organizationOwner', '组织管理员')}
          </h3>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {ownerInfo.email.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900">{ownerInfo.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">{t('orgInfo.contactAdmin', '如有问题可联系管理员')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 邀请人信息 */}
      {memberInfo?.invited_by_email && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {t('orgInfo.invitedBy', '邀请人')}
          </h3>
          <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-primary font-bold text-lg shadow-sm">
              {memberInfo.invited_by_email.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900">{memberInfo.invited_by_email}</p>
              <p className="text-sm text-gray-500">{t('orgInfo.invitedYouToJoin', '邀请您加入此组织')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
